import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mountain, MapPin, Calendar, Users, Loader2, Heart } from 'lucide-react';
import { toFeaturedSlug } from './FeaturedStay';
import TAGLINE_MAP from '../data/taglineData';
import AISearchBar from '../components/AISearchBar';
import SEOHead from '../components/SEOHead';
import HimalayanFestivalBanner from '../components/HimalayanFestivalBanner';
import { useWishlist } from '../hooks/useWishlist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_ADJECTIVES = ['Handpicked', 'Unique', 'Curated', 'Boutique', 'Hidden', 'Distinctive', 'Soulful'];

// Strip source branding from descriptions
const cleanDescription = (desc) => {
  if (!desc) return '';
  // Remove "HUSHSTAY X PROPERTY_NAME" prefix (brand + all-caps name glued to description)
  let cleaned = desc.replace(/HUSHSTAY\s*X\s*[A-Z\s']*?(?=[A-Z][a-z])/g, '');
  return cleaned.trim();
};

// Get custom tagline by slug, fallback to DB tagline
const getTagline = (stay, slug) => {
  return TAGLINE_MAP[slug] || stay.tagline || '';
};

const DiscoveredStays = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stays, setStays] = useState([]);
  const [allStays, setAllStays] = useState([]);
  const { isWishlisted, toggleWishlist, getLikeCount } = useWishlist();
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [adjectiveIndex, setAdjectiveIndex] = useState(0);
  const [adjectiveFade, setAdjectiveFade] = useState(true);
  const [highlightName, setHighlightName] = useState(searchParams.get('highlight') || '');

  useEffect(() => {
    const interval = setInterval(() => {
      setAdjectiveFade(false);
      setTimeout(() => {
        setAdjectiveIndex(prev => (prev + 1) % HERO_ADJECTIVES.length);
        setAdjectiveFade(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const openWhatsApp = (stayName) => {
    const message = `Hi! I'm interested in learning more about ${stayName} on GoHimalayan.`;
    window.open(`https://wa.me/919871155963?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getPartnerSlug = (displayName) => {
    if (!displayName) return null;
    const name = displayName.toLowerCase();
    if (name.includes('bluehouse')) return 'bluehouse';
    if (name.includes('eagle')) return 'eaglenest';
    if (name.includes('tra boutique') || (name.includes('tra') && !name.includes('trail') && !name.includes('ultra') && !name.includes('chakra'))) return 'tra';
    if (name.includes('himalayan homestead')) return 'homestead';
    if (name.includes('raju') && name.includes('cottage')) return 'rajucottage';
    if (name.includes('brown dipper') || name.includes('browndipper')) return 'browndipper';
    if (name.includes('villa aavaas') || name.includes('villaaavaas')) return 'villaaavaas';
    if (name.includes('srirangam')) return 'srirangam';
    return null;
  };

  useEffect(() => {
    fetchStays();
  }, []);

  // Fetch all stays once on mount for search autocomplete
  useEffect(() => {
    fetch(`${API_URL}/api/curated/stays?limit=200`)
      .then(r => r.json())
      .then(d => setAllStays(d.stays || []))
      .catch(() => {});
  }, []);

  // Interleave stays by badge category for visual variety
  const mixStaysByCategory = (staysList) => {
    const getBadgeCategory = (s) => {
      // Data-driven first — respect explicit partnership_status set via /set-stay-meta
      if (s.partnership_status === 'featured') return 'featured';
      if (s.partnership_status === 'valley_partner') return 'valley_partner';
      if (s.partnership_status !== 'partner') return 'discovered';
      // Legacy: existing partner stays bucketed by slug
      const slug = getPartnerSlug(s.display_name);
      if (slug === 'rajucottage') return 'valley_partner';
      if (slug === 'browndipper') return 'featured';
      if (slug === 'villaaavaas') return 'featured';
      if (slug === 'srirangam') return 'featured';
      return 'collective';
    };
    const buckets = { collective: [], featured: [], valley_partner: [], discovered: [] };
    staysList.forEach(s => buckets[getBadgeCategory(s)].push(s));
    const order = ['collective', 'discovered', 'valley_partner', 'featured'];
    const mixed = [];
    let added = true;
    while (added) {
      added = false;
      for (const cat of order) {
        if (buckets[cat].length > 0) {
          mixed.push(buckets[cat].shift());
          added = true;
        }
      }
    }
    return mixed;
  };

  const fetchStays = async () => {
    try {
      // Pull every active stay — we have 61+ now and the hardcoded 50 was
      // silently dropping ~11 cards (the newest scraped ones) off the public
      // catalogue. Server caps at 200 (le=200 in curated_routes.py).
      const url = `${API_URL}/api/curated/stays?limit=200`;
      const response = await fetch(url);
      const data = await response.json();
      setStays(mixStaysByCategory(data.stays || []));
    } catch (error) {
      console.error('Error fetching stays:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExperienceLabel = (types) => {
    const labels = {
      riverside: 'Riverside', forest: 'Forest', mountain_view: 'Mountain View',
      orchard: 'Orchard', orchards: 'Orchards', heritage: 'Heritage', wildlife: 'Wildlife',
      adventure: 'Adventure', mountain_retreat: 'Mountain Retreat', spiritual: 'Spiritual',
      trekking: 'Trekking', river: 'Riverside', birding: 'Birding', wellness: 'Wellness',
      food_forward: 'Food Forward', fishing: 'Fishing', lake: 'Lakeside',
      paragliding: 'Paragliding', trekking_gateway: 'Trekking Gateway',
      annapurna_view: 'Annapurna View', monasteries: 'Monastery Trails',
      stream: 'Stream-side', secluded: 'Secluded', peaceful: 'Peaceful',
      luxury: 'Luxury', stargazing: 'Stargazing', culinary: 'Culinary',
      high_altitude: 'High Altitude', photography: 'Photography', hiking: 'Hiking'
    };
    if (!types || types.length === 0) return 'Mountain Stay';
    const pretty = (t) => labels[t] || String(t).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return types.slice(0, 3).map(pretty).join(' · ');
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1];
  };

  const getBestMonths = (months) => {
    if (!months || months.length === 0) return 'Year-round';
    if (months.length > 6) return 'Most of the year';
    return months.map(m => getMonthName(m)).join(', ');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEOHead
        title="Discover Curated Stays Across the Himalayas"
        description={`Browse ${allStays.length} handpicked mountain stays — from forest retreats in Kumaon to riverside lodges in Tirthan Valley. Authentic properties, no chain hotels.`}
        url="/discovered"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "GoHimalayan Curated Stays",
          "description": "Handpicked mountain stays across the Indian Himalayas",
          "numberOfItems": allStays.length,
          "itemListElement": allStays.slice(0, 10).map((stay, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": stay.display_name,
            "url": `https://gohimalayan.com/stay/${stay.display_name?.split(',')[0]?.trim().toLowerCase().replace(/['']/g,'').replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}`
          }))
        }}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" strokeWidth={2.5} />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/wishlist"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                data-testid="wishlist-nav-link"
              >
                <Heart className="w-4 h-4" />
                <span className="hidden sm:inline">Wishlist</span>
              </Link>
              <Link 
                to="/"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero with Himalayan silhouette */}
      <section className="pt-32 pb-12 px-6 lg:px-8 relative overflow-hidden bg-gradient-to-b from-[#f0f4f8] to-[#FAFAFA]">
        {/* Himalayan mountain silhouette background */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <svg viewBox="0 0 1440 400" preserveAspectRatio="xMidYMax slice" className="w-full h-full" fill="#2C5F8D">
            <path d="M0,400 L0,280 L60,260 L120,290 L180,240 L220,270 L280,200 L320,230 L360,180 L400,210 L440,160 L480,190 L520,140 L560,170 L600,120 L640,150 L680,100 L720,80 L760,110 L800,70 L840,100 L880,60 L920,90 L960,50 L1000,80 L1040,40 L1080,70 L1120,30 L1160,60 L1200,80 L1240,50 L1280,90 L1320,60 L1360,100 L1400,70 L1440,110 L1440,400 Z" />
            <path d="M0,400 L0,320 L80,300 L160,330 L240,280 L320,310 L400,260 L480,290 L560,240 L640,270 L720,220 L800,250 L880,200 L960,230 L1040,180 L1120,210 L1200,190 L1280,220 L1360,200 L1440,230 L1440,400 Z" opacity="0.5" />
            <path d="M0,400 L0,350 L100,340 L200,360 L300,330 L400,350 L500,320 L600,340 L700,310 L800,330 L900,300 L1000,320 L1100,290 L1200,310 L1300,300 L1440,320 L1440,400 Z" opacity="0.3" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="max-w-xl mx-auto mb-8">
            <AISearchBar />
          </div>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            Discover{' '}
            <span
              className="inline-block italic text-[#2C5F8D]"
              style={{ transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)', opacity: adjectiveFade ? 1 : 0, transform: adjectiveFade ? 'translateY(0)' : 'translateY(4px)' }}
              data-testid="hero-adjective"
            >
              {HERO_ADJECTIVES[adjectiveIndex]}
            </span>{' '}
            Stays
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto mb-8">
            A curated selection of distinctive properties across the Himalayas. Each stay is chosen for character, authenticity, and connection to place.
          </p>
        </div>
      </section>

      {/* Stays Grid */}
      <section className="pb-20 px-6 lg:px-8 relative z-[1]">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#2C5F8D]" />
            </div>
          ) : (() => {
            let filtered = stays;
            // If a property was highlighted from landing page, move it to top
            if (highlightName) {
              const hl = highlightName.toLowerCase();
              const highlighted = filtered.filter(s => s.display_name?.toLowerCase() === hl);
              const rest = filtered.filter(s => s.display_name?.toLowerCase() !== hl);
              filtered = [...highlighted, ...rest];
            }
            return filtered.length === 0 ? (
            <div className="text-center py-20">
              <Mountain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No stays found. Check back soon as we add more properties.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((stay) => {
                // Stays can earn a Featured badge two ways:
                //   1) Hand-curated "discovered" stays where we've upgraded
                //      `partnership_status` to 'featured' via /set-stay-meta
                //   2) Legacy "partner" stays whose slug is in the original Featured set
                const partnerSlug = stay.partnership_status === 'partner' ? getPartnerSlug(stay.display_name) : null;
                const featuredSlug = !partnerSlug ? toFeaturedSlug(stay.display_name) : null;
                const taglineSlug = partnerSlug || featuredSlug;
                const customTagline = getTagline(stay, taglineSlug);
                const cleanDesc = cleanDescription(stay.description);
                const linkTo = partnerSlug ? `/collective/${partnerSlug}` : `/stay/${featuredSlug}`;
                // Badge resolution — data-driven via partnership_status, with a
                // fallback to the legacy hardcoded slug lists for backwards-compat.
                const isFeatured =
                  stay.partnership_status === 'featured' ||
                  (stay.partnership_status === 'partner' && partnerSlug && ['browndipper', 'villaaavaas', 'srirangam'].includes(partnerSlug));
                const isValleyPartner =
                  stay.partnership_status === 'valley_partner' ||
                  (stay.partnership_status === 'partner' && partnerSlug && ['rajucottage'].includes(partnerSlug));
                const isCollective =
                  stay.partnership_status === 'partner' && partnerSlug && !isFeatured && !isValleyPartner;
                return (
                <Link to={linkTo} key={stay.gohimalayan_id} className="bg-white rounded-2xl overflow-hidden border border-gray-100/60 hover:shadow-lg transition-all cursor-pointer group" data-testid={partnerSlug ? `partner-card-${partnerSlug}` : `featured-card-${featuredSlug}`}>
                  {/* Image */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-[#2C5F8D]/20 to-[#2C5F8D]/5 overflow-hidden relative">
                    {stay.images && stay.images[0] && stay.images[0].url ? (
                      <img 
                        src={stay.images[0].url} 
                        alt={stay.display_name}
                        className="w-full h-full object-cover img-enhance"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <Mountain className="w-12 h-12 text-[#2C5F8D]/30 mx-auto mb-2" />
                          <span className="text-xs text-[#2C5F8D]/50">{stay.valley}</span>
                        </div>
                      </div>
                    )}
                    {/* GoHimalayan Collective 100 watermark */}
                    {isCollective && (
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 pointer-events-none" style={{opacity: 0.85}}>
                        <div className="relative flex items-center bg-[#3a5a40]/80 backdrop-blur-sm px-3 py-1.5 rounded-md">
                          <span 
                            className="absolute right-2 font-bold text-white/15 leading-none select-none"
                            style={{fontSize: '36px', letterSpacing: '-2px'}}
                          >100</span>
                          <svg className="w-4 h-4 text-white/90 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                          </svg>
                          <div className="relative z-10 flex flex-col leading-tight">
                            <span className="text-[10px] font-semibold text-white tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
                            <span className="text-[7px] font-medium text-white/70 tracking-[0.2em] uppercase">Collective</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Featured badge */}
                    {isFeatured && (
                      <div className="absolute top-3 left-3 pointer-events-none" data-testid="featured-badge">
                        <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                          <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                          </svg>
                          <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Featured</span>
                        </div>
                      </div>
                    )}
                    {/* Valley Partner badge */}
                    {isValleyPartner && (
                      <div className="absolute top-3 left-3 pointer-events-none" style={{opacity: 0.9}}>
                        <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm border border-stone-100">
                          <svg className="w-3.5 h-3.5 text-[#3a5a40] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                          </svg>
                          <span className="text-[9px] font-semibold text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
                        </div>
                      </div>
                    )}
                    {/* Discovered by GoHimalayan — editorial badge fallback */}
                    {!isFeatured && !isValleyPartner && !isCollective && (
                      <div className="absolute top-3 left-3 pointer-events-none" data-testid="discovered-badge">
                        <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-md" style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                          <svg className="w-3 h-3 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                          </svg>
                          <span className="text-[9px] font-medium text-stone-500 tracking-[0.12em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Discovered</span>
                        </div>
                      </div>
                    )}
                    {/* Wishlist Heart */}
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(stay.gohimalayan_id); }}
                      className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full hover:bg-black/50 transition-colors"
                      data-testid={`wishlist-btn-${stay.gohimalayan_id}`}
                    >
                      <Heart className={`w-3.5 h-3.5 transition-colors ${isWishlisted(stay.gohimalayan_id) ? 'fill-red-500 text-red-500' : 'text-white/80'}`} />
                      <span className="text-[10px] text-white/80 font-medium">{getLikeCount(stay.gohimalayan_id)}</span>
                    </button>
                    {/* Thumbnail gallery for partner properties */}
                    {stay.partnership_status === 'partner' && stay.images && stay.images.length > 1 && (
                      <div className="absolute bottom-2 left-2 right-2 flex gap-1.5">
                        {stay.images.slice(1, 5).map((img, idx) => (
                          <div 
                            key={idx} 
                            className="flex-1 h-12 rounded-md overflow-hidden border-2 border-white/70 shadow-sm"
                          >
                            <img 
                              src={img.url} 
                              alt={`${stay.display_name} ${idx + 2}`}
                              className="w-full h-full object-cover img-enhance"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    {/* Experience Tags */}
                    <p className="text-xs text-[#2C5F8D] font-medium mb-2">
                      {getExperienceLabel(stay.experience_types)}
                    </p>
                    
                    {/* Name */}
                    <h3 
                      className="text-lg font-semibold text-gray-900 mb-1"
                      style={{fontFamily: 'Playfair Display, serif'}}
                    >
                      {stay.display_name}
                    </h3>
                    {stay.display_name?.toLowerCase().includes('brown dipper') && (
                      <p className="text-[10px] text-stone-400 italic -mt-0.5 mb-1" style={{fontFamily: 'Playfair Display, serif'}}>by Humming Woods</p>
                    )}
                    
                    {/* Tagline */}
                    {customTagline && (
                      <p className="text-sm text-gray-500 italic mb-2">
                        "{customTagline}"
                      </p>
                    )}
                    
                    {/* Description */}
                    {cleanDesc && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {expandedCards[stay.gohimalayan_id] 
                            ? cleanDesc 
                            : cleanDesc.slice(0, 150) + (cleanDesc.length > 150 ? '...' : '')
                          }
                        </p>
                        {cleanDesc.length > 150 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedCards(prev => ({
                                ...prev,
                                [stay.gohimalayan_id]: !prev[stay.gohimalayan_id]
                              }));
                            }}
                            className="text-xs text-[#2C5F8D] font-medium mt-1 hover:underline"
                            data-testid={`read-more-${stay.gohimalayan_id}`}
                          >
                            {expandedCards[stay.gohimalayan_id] ? 'Read less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {stay.region}, {stay.state}
                      </span>
                      {stay.altitude_meters && (
                        <span>{stay.altitude_meters}m</span>
                      )}
                    </div>
                    
                    {/* Best Months */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                      <Calendar className="w-3 h-3" />
                      <span>Best: {getBestMonths(stay.best_months)}</span>
                    </div>
                    
                    {/* Best For */}
                    {stay.best_for && stay.best_for.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                        <Users className="w-3 h-3" />
                        <span>{stay.best_for.map(b => b.replace('_', ' ')).join(', ')}</span>
                      </div>
                    )}
                    
                    {/* CTA */}
                    <div className={`w-full py-2.5 text-sm font-medium rounded-full flex items-center justify-center gap-2 transition-colors ${partnerSlug ? 'bg-[#3a5a40] text-white group-hover:bg-[#2C3E2D]' : 'bg-[#2C5F8D] text-white group-hover:bg-[#1e4a6e]'}`}>
                      View Full Story
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          );
          })()}
        </div>
      </section>

      {/* Note */}
      <section className="pb-20 px-6 lg:px-8" data-testid="discovered-editorial-note">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm text-gray-500 leading-relaxed">
            Properties featured on GoHimalayan are discovered, written about, and photographed editorially. <span className="font-medium text-gray-700">Featured properties don't pay to appear here.</span> If you're a host who'd like coverage removed or to formally join our partner program, write to <a href="mailto:editorial@gohimalayan.com" className="text-[#3a5a40] underline">editorial@gohimalayan.com</a> or <Link to="/editorial-policy" className="text-[#3a5a40] underline">read our full editorial policy</Link>.
          </p>
        </div>
      </section>

      {/* Himalayan Festival Calendar */}
      <HimalayanFestivalBanner />

      {/* Footer */}
      <footer className="py-8 px-6 lg:px-8 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Mountain className="h-5 w-5 text-[#2C5F8D]" />
            <span className="text-sm text-gray-500">© 2026 GoHimalayan</span>
          </div>
          <Link 
            to="/"
            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default DiscoveredStays;
